import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { ItemStatus, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';

export enum RedactionStatus {
  ENQUEUED = 'enqueued',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum RedactionResult {
  REDACTED = 'redacted',
  NOTHING_FOUND = 'nothing_found',
}

export interface RedactionSummary {
  status: RedactionStatus;
  result: RedactionResult | null;
  categories: string[];
  counts: Record<string, number>;
  nothingToRedact?: boolean;
  checksum?: string | null;
}

export interface RedactionState {
  summary: RedactionSummary | null;
  isAvailable: boolean;
  isStarting: boolean;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_REDACTION);

// Category ids are data, not field names: `counts` is keyed by them and `categories`
// carries them as values. Every wire payload is camelCased on the way in, which would
// leave `counts.awsKey` next to `categories: ['aws_key']`, so the keys are put back.
export const withCategoryIdKeys = (summary?: RedactionSummary | null): RedactionSummary | null => {
  if (!summary) {
    return null;
  }

  const counts = Object.entries(summary.counts || {}).reduce<Record<string, number>>((all, [key, count]) => {
    all[key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)] = count;
    return all;
  }, {});

  return { ...summary, counts };
};

export const useRedactionStore = defineStore('redaction', {
  state: (): RedactionState => ({
    summary: null,
    isAvailable: false,
    isStarting: false,
  }),

  getters: {
    isRunning: (state: RedactionState) => state.summary?.status === RedactionStatus.ENQUEUED || state.summary?.status === RedactionStatus.RUNNING,
    isRedacted: (state: RedactionState) => state.summary?.result === RedactionResult.REDACTED,
    hasFailed: (state: RedactionState) => state.summary?.status === RedactionStatus.FAILED,
  },

  actions: {
    initialize(summary?: RedactionSummary | null, isAvailable = false) {
      this.summary = withCategoryIdKeys(summary);
      this.isAvailable = isAvailable;
    },

    patch(summary?: RedactionSummary | null) {
      if (!summary) {
        return;
      }

      this.summary = withCategoryIdKeys(summary);

      // `start` marks the item editing as soon as a run is accepted. A run that stops
      // before it reserves the item never changes the item on the server, so no item
      // update follows and the player would stay "processing" until a reload.
      if (this.hasFailed) {
        const viewerItemStore = useViewerItemStore();
        if (viewerItemStore.item?.attributes?.status === ItemStatus.EDITING) {
          viewerItemStore.setItemStatus(ItemStatus.COMPLETED);
        }
      }
    },

    // The run itself reports through the item channel, so the response only has to
    // put the viewer into the running state.
    //
    // A second call while the first is in flight is dropped: the run is not visible to
    // the server's own in-progress check until it is created, so two creates can both
    // be accepted and the loser's failure becomes the newest summary.
    async start(itemId: string) {
      if (this.isStarting) {
        return false;
      }

      this.isStarting = true;

      // Pusher can deliver the run's own updates before this response does. Anything
      // that arrived in between is newer, so the response only fills in when nothing did.
      const summaryAtClick = this.summary;

      try {
        const response = await Api.item.redaction.create(itemId);
        if (this.summary === summaryAtClick) {
          this.patch(response.data.redaction?.attributes);
        }
        // The job marks the item editing once it picks the run up, and that update reaches
        // the page seconds later. Showing it now keeps the player in step with the row. A
        // run that already failed never reserved the item, so there is nothing to show.
        if (!this.hasFailed) {
          useViewerItemStore().setItemStatus(ItemStatus.EDITING);
        }
        return true;
      } catch (error) {
        debug('[redaction] could not start:', error);
        throw error;
      } finally {
        this.isStarting = false;
      }
    },
  },
});
